"use client";

import { useState } from "react";
import { Check, Star } from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { PRICING, formatDisplayPrice, getAnnualSavings, getAnnualSavingsPercent, getEffectiveMonthlyPrice, type BillingInterval } from "@/lib/pricing";

const plans = [
  {
    name: "FREE" as const,
    description: "Discover events and experiences near you",
    features: [
      "Participate in events and other digital asset experiences",
      "Earn digital asset rewards",
      "Participate in leaderboard challenges"
    ],
    buttonText: "Get Started Free",
    buttonVariant: "default" as const,
    popular: false
  },
  {
    name: "STARTER" as const,
    description: "Great for small teams and events",
    features: [
      "1 active campaign",
      "500 monthly assets",
      "Advanced analytics",
      "WAX blockchain",
      "Digital asset gating",
      "Export participants",
      "Priority email support"
    ],
    buttonText: "Get Started",
    buttonVariant: "default" as const,
    popular: false
  },
  {
    name: "PREMIUM" as const,
    description: "Best for growing businesses",
    features: [
      "3 active campaigns",
      "5,000 monthly assets",
      "POAP and additional blockchains",
      "3rd party integrations (Mailchimp, Meetup, Luma, and more)",
      "Premium gamification features",
      "9 AM - 5 PM ET premium chat support"
    ],
    buttonText: "Get Started",
    buttonVariant: "default" as const,
    popular: true
  }
];

export function PricingSection() {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("annual");

  return (
    <section id="pricing" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the perfect plan for your needs. All plans include unlimited locations per campaign
            and access to our digital asset reward system.
          </p>

          {/* Billing interval toggle */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
            <button
              type="button"
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                billingInterval === 'annual' ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  billingInterval === 'annual' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingInterval === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
              Annual
            </span>
            <span className="ml-1 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Save ~{getAnnualSavingsPercent('STARTER')}%
            </span>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto items-start">
          {plans.map((plan, index) => {
            const pricing = PRICING[plan.name];
            const savings = getAnnualSavings(plan.name);
            const effectiveMonthly = getEffectiveMonthlyPrice(plan.name, billingInterval);

            return (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                plan.popular
                  ? 'ring-2 ring-blue-600'
                  : 'hover:shadow-xl transition-shadow duration-300'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center whitespace-nowrap">
                    <Star className="w-4 h-4 mr-1" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{formatDisplayPrice(effectiveMonthly)}</span>
                  <span className="text-gray-600">/month</span>
                </div>
                {billingInterval === 'annual' && pricing.monthly > 0 && (
                  <p className="text-sm text-gray-500 line-through">
                    {formatDisplayPrice(pricing.monthly)}/month
                  </p>
                )}
                {billingInterval === 'annual' && savings > 0 && (
                  <p className="text-sm text-green-600 font-medium">
                    {formatDisplayPrice(pricing.annual)}/year — save {formatDisplayPrice(savings)}
                  </p>
                )}
                <p className="text-gray-600 mt-2">{plan.description}</p>
              </div>

              {/* IYK Starter Set bonus for Premium */}
              {plan.name === "PREMIUM" && billingInterval === 'annual' && (
                <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-3 text-center flex flex-col items-center gap-2">
                  <Image src="/images/integrations/iyk.png" alt="IYK" width={28} height={28} className="object-contain" />
                  <p className="text-sm font-semibold text-purple-700">
                    Free IYK Starter Set included
                  </p>
                  <p className="text-xs text-purple-600">$30 value — for new annual subscribers</p>
                </div>
              )}
              {plan.name === "PREMIUM" && billingInterval === 'monthly' && (
                <div className="mb-6 bg-gray-50 border border-gray-200 rounded-xl p-3 text-center flex flex-col items-center gap-2">
                  <Image src="/images/integrations/iyk.png" alt="IYK" width={24} height={24} className="object-contain opacity-60" />
                  <p className="text-xs text-gray-500">
                    Switch to annual billing to get a <span className="font-semibold text-gray-700">free IYK Starter Set</span> with your subscription
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="mb-8">
                <ul className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <Link href="/dashboard/campaigns/new" className="block">
                <Button
                  variant={plan.buttonVariant}
                  className={`w-full py-3 ${
                    plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </Link>

            </div>
          );
          })}

          {/* Enterprise card */}
          <div className="relative bg-gradient-to-b from-gray-900 to-gray-800 rounded-2xl shadow-lg p-8 text-white hover:shadow-xl transition-shadow duration-300">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">
                WHITE GLOVE
              </h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-white">Custom</span>
              </div>
              <p className="text-gray-300">On-site experience execution and support</p>
            </div>

            <div className="mb-8">
              <ul className="space-y-4">
                {[
                  "Everything in Premium",
                  "Whitelabel",
                  "Custom integrations",
                  "Dedicated account manager"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <Check className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button variant="outline" className="w-full py-3 bg-transparent border-white text-white hover:bg-white hover:text-gray-900">
              Contact Sales
            </Button>
          </div>
        </div>


      </div>
    </section>
  );
}
